/* Shared Outlook styles (the `<Outlook>` component).
 *
 * Everything is scoped under `.nw-ol` and the design tokens are defined there
 * too, so the component renders identically in the dashboard SPA AND inside the
 * embeddable widget's shadow DOM (which only has its own `--nw-*` tokens). Hosts
 * load this file: the dashboard imports `@netwatchai/chat-core/outlook.css`; the
 * widget inlines it into its shadow-root <style>. Ported from the dashboard
 * design (app.css) + a new day-summary overlay.
 *
 * LIGHT / DARK — the netwatch theming convention for shared components:
 *   • a DARK palette is the default (the dashboard is dark-only, and it's also
 *     the widget's dark theme);
 *   • the widget cascades `.nw-theme-light` / `.nw-theme-dark` onto `.nw-root`
 *     (an ancestor of `.nw-ol`), so a `.nw-theme-light .nw-ol` block flips the
 *     tokens to a light palette with no JS — see the bottom of this file.
 * Keep surface colours in tokens (never hard-code #fff / a dark hex in a rule
 * or an SVG fill); use `var(--card)` / `var(--ink)` so they invert with the
 * theme. New shared components should follow the same pattern. */

.nw-ol {
  /* design tokens — DARK palette (default). Light overrides are at EOF. */
  --card: #141b25;
  --line: rgba(255, 255, 255, 0.07);
  --line2: rgba(255, 255, 255, 0.12);
  --ink: #e7ecf3;
  --soft: #98a4b4;
  --faint: #5e6b7c;
  --accent: #5b93e8;
  --green: #46b27e;
  --amber: #d99a3a;
  --orange: #dd7a38;
  --red: #d9605c;
  /* distinct High vs Disaster reds — mirror the SEVERITY registry (severity.ts) */
  --sev4: #E97659;
  --sev5: #E45959;
  --track: rgba(255, 255, 255, 0.07); /* meter / sparkline track */
  --inset: rgba(255, 255, 255, 0.02); /* faint inset panel (day hero) */
  /* A well sunk INTO a card, toward the ground — `--inset` lifts in dark,
     which reads as a box on the card rather than a recess in it. */
  --recess: rgba(0, 0, 0, 0.22);
  /* Chart series: measured-usage line. A darker amber step than --amber —
     validated (dataviz six checks) against BOTH surfaces alongside --accent,
     so it needs no light override. Never reuse status reds for a series. */
  --chart-measured: #c08430;
  --scrim: rgba(5, 9, 15, .52);
  /* Declared, not inherited: the widget renders .nw-ol inside a shadow root
     where the SPA's :root never reaches. Each falls back to the host's value
     when there is one. */
  --chip-bg: var(--nw-chip-bg, rgba(255, 255, 255, .03));
  --code-bg: var(--nw-code-bg, rgba(255, 255, 255, .07));
  --hover: var(--nw-hover, rgba(255, 255, 255, .04));
  --font-mono: var(--nw-font-mono, 'JetBrains Mono', ui-monospace, monospace);
  /* Third series slot (day-modal "Resolved" line): violet, validated with
     --accent + --chart-measured against both surfaces (2026-08-26). Light
     override at EOF. */
  --chart-third: #9085e9;
  --font-display: 'Saira', system-ui, sans-serif;
  --font-body: 'Barlow', system-ui, sans-serif;
  /* <OutlookTeaser> card: the tinted panel, the illustration's halo and its
     text placeholders. Brand-tinted so they read as one family with the
     gradient button; light values at EOF. */
  --olt-panel: rgba(99, 102, 241, .09);
  --olt-halo: rgba(99, 102, 241, .18);
  --olt-ph: rgba(255, 255, 255, .09);

  position: relative;
  color: var(--ink);
  font-family: var(--font-body);
}
.nw-ol * { box-sizing: border-box; }

/* hero */
.nw-ol .ohero { display: flex; flex-direction: column; align-items: center; text-align: center; background: var(--card); border: 1px solid var(--line); border-radius: 16px; padding: 26px 24px; }
.nw-ol .serverwrap { position: relative; display: inline-block; margin-bottom: 15px; }
.nw-ol .serverwrap .moodface { position: absolute; right: -2px; bottom: 0; filter: drop-shadow(0 0 8px rgba(217, 154, 58, .38)); }
/* "Now" tag pinned to the rack art — marks the hero face as the LIVE reading
   (the 7-day strip under it is history). Tinted by the same tone as the face,
   so the tag and the mouth always agree. */
.nw-ol .serverwrap .nowtag { position: absolute; left: 42px; bottom: 6px; font-family: var(--font-display); font-size: 9.5px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; line-height: 1; color: var(--amber); background: var(--card); border: 1px solid currentColor; border-radius: 5px; padding: 3px 6px; box-shadow: 0 1px 6px rgba(0, 0, 0, .3); }
.nw-ol .serverwrap .nowtag.nt-great { color: var(--green); }
.nw-ol .serverwrap .nowtag.nt-warn { color: var(--orange); }
.nw-ol .serverwrap .nowtag.nt-bad { color: var(--red); }
.nw-ol .ocap .om { font-family: var(--font-display); font-size: 19px; font-weight: 600; }
.nw-ol .ocap .os { font-size: 13.5px; color: var(--soft); margin-top: 3px; }
.nw-ol .ocap .ores { display: inline-flex; align-items: center; gap: 7px; margin-top: 14px; font-size: 12px; font-weight: 600; color: var(--green); border: 1px solid rgba(70, 178, 126, .3); border-radius: 999px; padding: 5px 12px; }

/* Overall health meter — the confidence score under its user-facing name:
   N/100 + letter grade, gradient Poor→Healthy track with a position dot. */
.nw-ol .envh { background: var(--card); border: 1px solid var(--line); border-radius: 14px; padding: 14px 16px 11px; margin-top: 13px; }
.nw-ol .envh-head { display: flex; align-items: center; gap: 7px; }
.nw-ol .envh-title { font-family: var(--font-display); font-size: 13px; font-weight: 600; }
.nw-ol .envh-info { display: inline-flex; align-items: center; padding: 0; background: none; border: none; color: var(--faint); cursor: pointer; }
.nw-ol .envh-info:hover { color: var(--soft); }
/* The grade is a tinted tag (fill + border from its own hue) so it reads as
   the live state at a glance; the readout to its left names the grade it
   moved from. The N/100 number is in both tooltips, not in the header. */
.nw-ol .envh-grade { flex: none; font-family: var(--font-display); font-size: 13px; font-weight: 700; line-height: 1; letter-spacing: .02em; border: 1px solid color-mix(in srgb, currentColor 55%, transparent); border-radius: 7px; padding: 5px 10px; background: color-mix(in srgb, currentColor 13%, transparent); }
.nw-ol .envh .g-a { color: var(--green); }
.nw-ol .envh .g-b { color: var(--amber); }
.nw-ol .envh .g-c { color: var(--orange); }
.nw-ol .envh .g-d,
.nw-ol .envh .g-f { color: var(--red); }
.nw-ol .envh-bar { position: relative; height: 8px; border-radius: 6px; margin-top: 18px; background: linear-gradient(90deg, var(--red), var(--orange) 38%, var(--amber) 64%, var(--green)); box-shadow: inset 0 1px 2px rgba(0, 0, 0, .28); }
/* faint grade-boundary ticks (D/C/B/A at 50/60/80/90) */
.nw-ol .envh-tick { position: absolute; top: -3px; bottom: -3px; width: 1px; background: var(--line2); transform: translateX(-.5px); }
/* position dot: white core, card ring, grade-coloured halo; slides in from 0
   on mount (keyframe start overrides the inline `left` for the first beat). */
/* Where the score sat at the last compiled close, and the ground it has
   covered since. Both are read POSITIONALLY: the track under them is already
   the semaphore gradient, so the direction takes its meaning from where the
   trail goes, not from a colour of its own (DESIGN.md's Semaphore Rule —
   green/amber/red describe the environment's health; a delta is a
   measurement of it, and a green "+10" beside a red 50 would be the louder
   of two claims). The mark is deliberately taller than the live dot so it
   stays legible when a one-point move parks the two on top of each other. */
.nw-ol .envh-was { position: absolute; z-index: 2; top: 0; bottom: 0; width: 2px; transform: translateX(-50%); }
/* Two strokes bracketing the track rather than one needle across it: a
   one-point move parks the mark on the live dot, and a needle there sliced
   the dot in half and read as a rendering fault. Bracketed, the mark keeps
   clear of the dot at every distance and still says where the score sat. */
.nw-ol .envh-was::before, .nw-ol .envh-was::after { content: ''; position: absolute; left: 0; width: 2px; height: 8px; border-radius: 2px; background: color-mix(in srgb, var(--ink) 82%, transparent); }
.nw-ol .envh-was::before { bottom: calc(100% + 3px); }
.nw-ol .envh-was::after { top: calc(100% + 3px); }
.nw-ol .envh-trail { position: absolute; z-index: 0; top: calc(50% - 1px); height: 2px; border-radius: 2px; background: color-mix(in srgb, var(--ink) 72%, transparent); animation: nw-ol-envh-trail .9s cubic-bezier(.22, 1, .36, 1) both; }
@keyframes nw-ol-envh-trail { from { transform: scaleX(0); } }
.nw-ol .envh-dot { position: absolute; z-index: 1; top: 50%; width: 14px; height: 14px; border-radius: 50%; background: #fff; border: 2px solid var(--card); transform: translate(-50%, -50%); box-shadow: 0 0 0 3px var(--envh-glow, rgba(70, 178, 126, .38)), 0 1px 5px rgba(0, 0, 0, .4); animation: nw-ol-envh-in .9s cubic-bezier(.22, 1, .36, 1); }
/* The entrance is the trend: the dot starts on the last close and travels to
   the live reading, so the card shows the move once before settling. Falls
   back to the floor when there is no baseline to start from. */
@keyframes nw-ol-envh-in { from { left: var(--envh-from, 1.5%); } }
/* --soft, not --faint: at 10.5px the faint step is 3.2:1 on the card, under
   AA, and it now shares a row with a live trend reading. The legend stays a
   step back from that reading through size and weight instead of contrast. */
.nw-ol .envh-ends { display: flex; justify-content: space-between; align-items: center; gap: 10px; font-size: 10.5px; color: var(--soft); margin-top: 13px; }
/* The move, in words, directly under the move itself. Set a step above the
   static Poor/Healthy legend beside it — a live reading outranks a caption —
   with the magnitude in display ink so it reads as data, not prose. */
/* Change readout, right-aligned in the header beside the grade tag. */
.nw-ol .envh-trend { margin-left: auto; display: inline-flex; align-items: center; gap: 5px; min-width: 0; font-size: 11.5px; font-weight: 500; color: var(--soft); white-space: nowrap; }
/* Only the arrow takes a colour: a rise is green, a fall red, and the level
   dash recedes. The words beside it stay neutral so the tag keeps the hue. */
.nw-ol .envh-trend svg { flex: none; color: var(--faint); }
.nw-ol .envh-trend.up svg { color: var(--green); }
.nw-ol .envh-trend.down svg { color: var(--red); }
@media (prefers-reduced-motion: reduce) { .nw-ol .envh-dot, .nw-ol .envh-trail { animation: none; } }

/* posture bands */
.nw-ol .bands { display: flex; gap: 5px; margin-top: 13px; }
.nw-ol .band { flex: 1; text-align: center; font-family: var(--font-display); font-size: 11px; font-weight: 600; color: var(--faint); border: 1px solid var(--line); border-radius: 9px; padding: 8px 4px; transition: .15s; }
/* Active band highlight follows the current tone (ok → amber default). */
.nw-ol .band.on { background: rgba(217, 154, 58, .14); border-color: rgba(217, 154, 58, .45); color: var(--amber); animation: nw-ol-glowAmber 2.6s ease-in-out infinite; }
.nw-ol .band.on.bt-great { background: rgba(70, 178, 126, .14); border-color: rgba(70, 178, 126, .45); color: var(--green); animation: nw-ol-glowGreen 2.8s ease-in-out infinite; }
.nw-ol .band.on.bt-warn { background: rgba(221, 122, 56, .14); border-color: rgba(221, 122, 56, .5); color: var(--orange); animation: nw-ol-glowOrange 2s ease-in-out infinite; }
.nw-ol .band.on.bt-bad { background: rgba(217, 96, 92, .16); border-color: rgba(217, 96, 92, .6); color: var(--red); animation: nw-ol-glowRed 1.1s ease-in-out infinite; }
@keyframes nw-ol-glowAmber { 0%, 100% { box-shadow: 0 0 0 rgba(217, 154, 58, 0); } 50% { box-shadow: 0 0 13px rgba(217, 154, 58, .5); } }
@keyframes nw-ol-glowGreen { 0%, 100% { box-shadow: 0 0 0 rgba(70, 178, 126, 0); } 50% { box-shadow: 0 0 13px rgba(70, 178, 126, .5); } }
@keyframes nw-ol-glowOrange { 0%, 100% { box-shadow: 0 0 0 rgba(221, 122, 56, 0); } 50% { box-shadow: 0 0 14px rgba(221, 122, 56, .55); } }
@keyframes nw-ol-glowRed { 0%, 100% { box-shadow: 0 0 5px rgba(217, 96, 92, .45); } 50% { box-shadow: 0 0 22px rgba(217, 96, 92, .95); } }
/* Clickable bands (the host wired `onViewBand`): same look as the plain band,
   plus the affordances of a link into the incidents feed at that severity. */
.nw-ol button.band { margin: 0; background: none; line-height: inherit; cursor: pointer; }
.nw-ol button.band:not(.on):hover { color: var(--ink); border-color: var(--line2); }
.nw-ol button.band.on:hover { filter: brightness(1.12); }
.nw-ol button.band:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
/* Respect users who ask for less motion — freeze the band pulse for them. */
@media (prefers-reduced-motion: reduce) { .nw-ol .band.on { animation: none; } }

/* tiles */
.nw-ol .tiles { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 14px; }
.nw-ol .tile { background: var(--card); border: 1px solid var(--line); border-radius: 13px; padding: 16px; }
.nw-ol .tile .tl { font-size: 12.5px; font-weight: 600; color: var(--soft); display: flex; align-items: center; gap: 8px; }
.nw-ol .tile .tv { font-family: var(--font-display); font-size: 30px; font-weight: 700; line-height: 1; margin: 11px 0 3px; }
.nw-ol .tile .tv.amber { color: var(--amber); }
.nw-ol .tile .tv.orange { color: var(--orange); }
.nw-ol .tile .tv.red { color: var(--red); }
.nw-ol .tile .tv.sev4 { color: var(--sev4); }
.nw-ol .tile .tv.sev5 { color: var(--sev5); }
/* Sub-line urgency phrase (under the number): worth a look / look soon / act now. */
.nw-ol .tile .ts .urg { font-weight: 600; }
.nw-ol .tile .ts .urg-amber { color: var(--amber); }
.nw-ol .tile .ts .urg-orange { color: var(--orange); }
.nw-ol .tile .ts .urg-red { color: var(--red); }
.nw-ol .tile .tnum { font-variant-numeric: tabular-nums; }
.nw-ol .tile .ts { font-size: 12px; color: var(--faint); }
.nw-ol .tile .ts b { color: var(--ink); font-weight: 600; }
/* Word beside the headline count ("3 hosts") — plain ink; only the number
   carries the severity colour. */
.nw-ol .tile .tv .tsev { font-family: var(--font-body); font-size: 12.5px; font-weight: 600; margin-left: 7px; letter-spacing: 0.02em; color: var(--ink); }
/* Stacked severity bar: one segment per non-empty severity (hosts at their
   WORST active severity), worst → least left-to-right. 2px surface gaps +
   per-segment tooltips carry identity — never colour alone. */
.nw-ol .tile .sevbar { display: flex; gap: 2px; height: 6px; border-radius: 5px; overflow: hidden; margin: 10px 0 6px; }
.nw-ol .tile .sevbar i { display: block; height: 100%; min-width: 3px; }
.nw-ol .tile .sevbar .sv-disaster { background: var(--sev5); }
.nw-ol .tile .sevbar .sv-high { background: var(--sev4); }
.nw-ol .tile .sevbar .sv-moderate { background: var(--amber); }
.nw-ol .tile .sevbar .sv-low { background: var(--accent); }
.nw-ol .tile .sevbar .sv-unclassified { background: var(--faint); }
.nw-ol .tile .meter { height: 6px; border-radius: 5px; background: var(--track); overflow: hidden; margin: 11px 0 3px; }
.nw-ol .tile .meter i { display: block; height: 100%; border-radius: 5px; background: linear-gradient(90deg, var(--amber), var(--red)); }
/* Drive-filling tile states: solid red when the worst drive is urgent (≤7d),
   green when nothing is filling. */
.nw-ol .tile .meter.red i { background: var(--red); }
.nw-ol .tile .meter.ok i { background: var(--green); }
/* Recent-spike marker on the drive name (a sudden fill above its normal rate). */
.nw-ol .tile .nw-ol-jump { margin-left: 5px; font-size: 11px; color: var(--red); }
.nw-ol .tile .tv2 { font-family: var(--font-display); font-size: 13.5px; font-weight: 500; color: var(--soft); margin: 11px 0 0; }
.nw-ol .tile .tv2 b { color: var(--ink); font-weight: 600; }
.nw-ol .tile .more2 { font-size: 12px; font-weight: 600; color: var(--accent); margin-top: 9px; cursor: pointer; }

/* posture line — the confidence payload's one-sentence summary of the whole
   environment, in the slot the alert-cluster card takes when the client has a
   cluster (that card brings its own alert-cluster.css). Inert: a sentence with
   nothing behind it, so the row is neither a link nor a button. */
.nw-ol .insight { display: flex; align-items: flex-start; gap: 13px; background: var(--card); border: 1px solid var(--line); border-radius: 13px; padding: 14px 16px; margin-top: 14px; color: inherit; }
.nw-ol .insight .gl { width: 38px; height: 38px; flex: none; border-radius: 11px; display: grid; place-items: center; background: color-mix(in srgb, var(--accent) 10%, transparent); border: 1px solid color-mix(in srgb, var(--accent) 22%, transparent); }
.nw-ol .insight .ib { container: ibody / inline-size; flex: 1; min-width: 0; }
.nw-ol .insight .il { display: flex; flex-wrap: wrap; align-items: center; gap: 2px; row-gap: 4px; font-family: var(--font-display); font-size: 10.5px; font-weight: 600; letter-spacing: .12em; text-transform: uppercase; color: var(--accent); }
.nw-ol .insight .ih { font-size: 13.5px; margin-top: 2px; }

/* 7-day strip */
.nw-ol .week2 { margin-top: 18px; }
.nw-ol .week2 .wt { font-family: var(--font-display); font-size: 11px; font-weight: 600; letter-spacing: .1em; text-transform: uppercase; color: var(--faint); margin: 0 2px 12px; display: flex; justify-content: space-between; }
.nw-ol .days2 { display: grid; grid-template-columns: repeat(7, 1fr); gap: 6px; }
.nw-ol .d2 { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 12px 2px; border: 1px solid var(--line); border-radius: 12px; background: var(--card); cursor: pointer; transition: border-color .15s; }
.nw-ol .d2:hover { border-color: var(--line2); }
/* days with no snapshot aren't clickable — drop the affordance */
.nw-ol .d2.empty { cursor: default; }
.nw-ol .d2.empty:hover { border-color: var(--line); }
.nw-ol .d2 .dw { font-size: 10.5px; letter-spacing: .06em; text-transform: uppercase; color: var(--faint); font-weight: 600; }
.nw-ol .d2 .dn { font-family: var(--font-display); font-size: 14px; font-weight: 700; }
.nw-ol .d2 .dn.amber { color: var(--amber); }
.nw-ol .d2 .dn.red { color: var(--red); }
.nw-ol .d2 .dn.c-sev5 { color: var(--sev5); }
.nw-ol .d2 .dn.c-sev4 { color: var(--sev4); }
.nw-ol .d2 .dn.c-soft { color: var(--soft); }
/* strip footer: "N incidents still open" + overdue pill */
.nw-ol .wfoot { display: flex; align-items: center; gap: 9px; margin: 12px 2px 0; font-size: 12.5px; }
.nw-ol .wfoot b { font-weight: 600; }
.nw-ol .wover { display: inline-flex; align-items: center; gap: 6px; font-family: var(--font-display); font-size: 11px; font-weight: 700; color: var(--amber); border: 1px solid rgba(217, 154, 58, .45); border-radius: 999px; padding: 3px 6px 3px 8px; }
.nw-ol .wover .nw-sev-icon { flex: none; }
/* pill tint follows the worst OVERDUE severity (registry ramp): Disaster deep
   red, High salmon, Warning/Average the amber default, below that soft. */
.nw-ol .wover.sv5 { color: var(--sev5); border-color: rgba(228, 89, 89, .55); }
.nw-ol .wover.sv4 { color: var(--sev4); border-color: rgba(233, 118, 89, .5); }
.nw-ol .wover.svlow { color: var(--soft); border-color: var(--line2); }
.nw-ol .wover-link { background: none; border: none; padding: 0; font: inherit; color: inherit; cursor: pointer; }
.nw-ol .wover-link:hover { text-decoration: underline; }
.nw-ol .wover-x { background: none; border: none; padding: 0 2px; font-size: 13px; line-height: 1; color: var(--faint); cursor: pointer; }
.nw-ol .wover-x:hover { color: var(--soft); }
.nw-ol .d2 svg { width: 24px; height: 24px; }
.nw-ol .d2 .d2emo { font-size: 20px; line-height: 1; color: var(--faint); height: 24px; display: grid; place-items: center; }
.nw-ol .d2.sel { box-shadow: inset 0 0 0 1px rgba(217, 154, 58, .4); background: rgba(217, 154, 58, .08); }
/* Worst-severity tint overrides: Disaster deep red, High salmon (matches the
   --sev5/--sev4 registry reds); lower severities keep the amber default. */
.nw-ol .d2.sel.s5 { box-shadow: inset 0 0 0 1px rgba(228, 89, 89, .55); background: rgba(228, 89, 89, .1); }
.nw-ol .d2.sel.s4 { box-shadow: inset 0 0 0 1px rgba(233, 118, 89, .5); background: rgba(233, 118, 89, .09); }
.nw-ol .d2.sel .dw { color: var(--amber); }

/* composer (self-contained — not the host's global .composer) */
.nw-ol .composer { display: flex; align-items: center; gap: 10px; margin-top: 18px; width: 100%; padding: 7px 7px 7px 16px; border-radius: 14px; background: var(--card); border: 1px solid var(--line); }
.nw-ol .composer:focus-within { border-color: var(--line2); }
.nw-ol .composer input { flex: 1; background: none; border: none; outline: none; color: var(--ink); font-family: var(--font-body); font-size: 14px; min-width: 0; }
.nw-ol .composer input::placeholder { color: var(--faint); }
.nw-ol .composer .send { width: 38px; height: 38px; flex: none; border-radius: 10px; border: none; cursor: pointer; display: grid; place-items: center; color: #fff; background: var(--accent); transition: .15s; }
.nw-ol .composer .send:hover { filter: brightness(1.08); }

/* loading / error hero variants */
.nw-ol .o-retry { width: auto; height: 38px; padding: 0 16px; border-radius: 10px; border: none; cursor: pointer; color: #fff; background: var(--accent); font-weight: 600; margin-top: 12px; }

/* --- drill-down dialogs (day-summary, affected-hosts, disks, cluster) ----- */
/* Modal: full-viewport frosted scrim + centered card. Dismissal lives in
   OverlayDialog (×, Escape, any press outside the card). The widget's shadow
   CSS re-contains the scrim to its panel (CHAT_CORE_OVERRIDES in
   NetwatchWidget.tsx) — a fixed scrim there would dim the customer's page. */
.nw-ol-dim { position: fixed; inset: 0; z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 24px; background: var(--scrim); backdrop-filter: blur(7px); -webkit-backdrop-filter: blur(7px); animation: nw-ol-fade .15s ease; }
.nw-ol-dialog { position: relative; width: min(760px, 100%); max-height: 100%; overflow-y: auto; background: var(--card); border: 1px solid var(--line2); border-radius: 16px; padding: 18px; box-shadow: 0 18px 50px -14px rgba(0, 0, 0, .55); animation: nw-ol-pop .18s ease; scrollbar-width: thin; scrollbar-color: var(--line2) transparent; }
/* Same slim scrollbar the app uses everywhere else (stream/faqpanel/histlist),
   on theme tokens so light mode follows. */
.nw-ol-dialog::-webkit-scrollbar { width: 9px; }
.nw-ol-dialog::-webkit-scrollbar-thumb { background: var(--line2); border-radius: 8px; }
.nw-ol-dialog::-webkit-scrollbar-track { background: transparent; }
@keyframes nw-ol-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes nw-ol-pop { from { opacity: 0; transform: translateY(10px) scale(.97); } to { opacity: 1; transform: none; } }
.nw-ol-dlg-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 14px; }
.nw-ol-dlg-close { width: 30px; height: 30px; flex: none; border-radius: 9px; border: 1px solid var(--line); background: none; color: var(--soft); font-size: 13px; line-height: 1; cursor: pointer; }
.nw-ol-dlg-close:hover { border-color: var(--line2); color: var(--ink); }
.nw-ol-day-title { font-family: var(--font-display); font-size: 14px; font-weight: 600; }
.nw-ol-day-hero { display: flex; align-items: center; gap: 14px; background: var(--inset); border: 1px solid var(--line); border-radius: 13px; padding: 16px; margin-bottom: 14px; }
.nw-ol-day-score { font-family: var(--font-display); font-size: 28px; font-weight: 700; line-height: 1; }
.nw-ol-day-label { font-size: 12.5px; color: var(--soft); margin-top: 3px; }
.nw-ol-day-resolved { font-size: 13.5px; color: var(--soft); text-align: center; padding: 10px; margin-bottom: 14px; background: var(--inset); border: 1px solid var(--line); border-radius: 11px; }
.nw-ol-day-resolved b { font-family: var(--font-display); font-size: 17px; font-weight: 700; color: var(--green); }
.nw-ol-day-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 16px; }
.nw-ol-stat { background: var(--card); border: 1px solid var(--line); border-radius: 11px; padding: 11px; text-align: center; }
.nw-ol-stat .n { font-family: var(--font-display); font-size: 20px; font-weight: 700; }
.nw-ol-stat .n.bad { color: var(--red); }
.nw-ol-stat .c { font-size: 11px; color: var(--faint); margin-top: 2px; }
.nw-ol-sect { font-family: var(--font-display); font-size: 11px; font-weight: 600; letter-spacing: .1em; text-transform: uppercase; color: var(--faint); margin: 4px 2px 8px; }
.nw-ol-sev { display: flex; align-items: center; gap: 9px; padding: 7px 2px; border-bottom: 1px solid var(--line); }
.nw-ol-sev .nw-sev-icon,
.nw-ol-host-head .nw-sev-icon,
.nw-ol-host-prob .nw-sev-icon,
.nw-ol .tile .tl .nw-sev-icon { flex: none; }
.nw-ol-sev .nm { flex: 1; font-size: 12.5px; color: var(--soft); }
.nw-ol-sev .ct { font-family: var(--font-display); font-weight: 700; font-variant-numeric: tabular-nums; }
.nw-ol-muted { font-size: 12.5px; color: var(--faint); padding: 8px 2px; }
/* Day-modal trend chart: export/expand row above the plot. The shared
   .nw-nchart-act buttons are hover-revealed inside a chat bubble; here there
   is no .nw-nchart-wrap hover context, so they stay visible. */
.nw-ol-daychart-head { display: flex; justify-content: flex-end; margin-bottom: 2px; }
.nw-ol-daychart .nw-nchart-act { opacity: 1; }
.nw-ol-daychart-hint { font-size: 10.5px; color: var(--faint); padding: 4px 2px 0; text-align: right; }

/* --- affected-hosts overlay (search + grouped hosts + paged) -------------- */
.nw-ol-hosts-search { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; padding: 8px 12px; border-radius: 11px; background: var(--inset); border: 1px solid var(--line); color: var(--faint); }
.nw-ol-hosts-search svg { width: 15px; height: 15px; flex: none; }
.nw-ol-hosts-search input { flex: 1; min-width: 0; background: none; border: none; outline: none; color: var(--ink); font-family: var(--font-body); font-size: 13px; }
.nw-ol-hosts-search input::placeholder { color: var(--faint); }
.nw-ol-hostlist { display: flex; flex-direction: column; gap: 10px; }
.nw-ol-host { background: var(--card); border: 1px solid var(--line); border-radius: 13px; padding: 13px 14px; }
.nw-ol-host-head { display: flex; align-items: center; gap: 9px; }
.nw-ol-host .dot { width: 9px; height: 9px; border-radius: 50%; flex: none; }

.nw-ol-host-name { flex: 1; min-width: 0; font-family: var(--font-display); font-size: 14px; font-weight: 600; color: var(--ink); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nw-ol-host-count { flex: none; font-size: 11.5px; font-weight: 600; color: var(--faint); }
/* Disk cards put the drive letter right beside the host name (not far right),
   so the name shrinks to its text and the drive takes the leftover width. */
.nw-ol-host-name.is-disk { flex: 0 1 auto; }
.nw-ol-disk-drive { flex: 1; min-width: 0; margin-left: -4px; font-family: var(--font-display); font-size: 14px; font-weight: 700; color: var(--ink); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nw-ol-host-probs { display: flex; flex-direction: column; gap: 6px; margin-top: 9px; }
.nw-ol-host-prob { display: flex; align-items: center; gap: 8px; }
.nw-ol-host-prob .nm { flex: 1; min-width: 0; font-size: 12.5px; color: var(--soft); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nw-ol-host-prob .ago { flex: none; font-size: 11px; color: var(--faint); }
.nw-ol-why-btn { display: inline-flex; align-items: center; gap: 6px; margin-top: 12px; padding: 7px 13px; border-radius: 9px; border: 1px solid rgba(91, 147, 232, .35); background: rgba(91, 147, 232, .1); color: var(--accent); font-family: var(--font-body); font-size: 12.5px; font-weight: 600; cursor: pointer; transition: .15s; }
.nw-ol-why-btn:hover { background: rgba(91, 147, 232, .18); }
.nw-ol-why-btn svg { width: 15px; height: 15px; }
.nw-ol-pager { display: flex; align-items: center; justify-content: center; gap: 14px; margin-top: 14px; font-size: 12px; color: var(--soft); }
.nw-ol-pager button { padding: 5px 11px; border-radius: 8px; border: 1px solid var(--line); background: none; color: var(--ink); font-family: var(--font-body); font-size: 12px; font-weight: 600; cursor: pointer; }
.nw-ol-pager button:hover:not(:disabled) { border-color: var(--line2); }
.nw-ol-pager button:disabled { opacity: .4; cursor: not-allowed; }

/* filling-disks overlay: per-row usage meter (the .tile .meter rules are scoped
   to tiles, so the overlay needs its own copy). */
.nw-ol-disk-meter { margin: 9px 0 2px; }
.nw-ol-disk-meter .meter { height: 6px; border-radius: 5px; background: var(--track); overflow: hidden; }
.nw-ol-disk-meter .meter i { display: block; height: 100%; border-radius: 5px; background: linear-gradient(90deg, var(--amber), var(--red)); }
.nw-ol-disk-meter .meter.red i { background: var(--red); }
/* Projected-fill chart revealed by the per-drive "Trend" toggle. The line is
   the forecast already in the row (current level → projected full), drawn with
   Outlook tokens so it inverts with the light/dark theme like everything else. */
.nw-ol-proj { margin-top: 11px; background: var(--inset); border: 1px solid var(--line); border-radius: 10px; padding: 8px 8px 4px; animation: nw-ol-fade .15s ease; }
/* Chart drawing (lines, threshold, now-divider, ticks) lives in the shared
   NwLineChart (Chart.js) component, which resolves --chart-measured/--accent/
   --red/--ink/--faint/--line2/--card from this cascade at render time. */
.nw-ol-proj-msg { font-size: 11px; color: var(--faint); padding: 10px 4px; text-align: center; }
/* Zoom segmented control for the trend window (30d … 1yr). */
.nw-ol-seg { display: flex; gap: 3px; background: var(--track); border: 1px solid var(--line); border-radius: 9px; padding: 3px; margin-bottom: 8px; }
.nw-ol-seg-btn { flex: 1; background: none; border: none; border-radius: 7px; padding: 5px 4px; font-family: var(--font-body); font-size: 11px; font-weight: 600; color: var(--soft); cursor: pointer; transition: background .15s, color .15s; }
.nw-ol-seg-btn:hover { color: var(--ink); }
.nw-ol-seg-btn.is-active { background: var(--card); color: var(--accent); }
/* The "Drive filling" tile is a button into the list — show it's clickable. */
.nw-ol .tile.nw-ol-tile-click { cursor: pointer; transition: border-color .15s; }
.nw-ol .tile.nw-ol-tile-click:hover { border-color: var(--line2); }

/* --- COMPACT SUMMARY (<OutlookSummary>) ------------------------------------
 * The Outlook's first screen on a surface that isn't the Outlook: the widget's
 * chat landing, above the composer. Same hero, bands and condition tile as the
 * page — only tighter, because it shares a 400px-wide panel with a chat input.
 * Overrides only: every colour, tone class and the light-theme flip come from
 * the blocks above, so the two surfaces cannot drift apart. */
.nw-ol.nw-olsum { width: 100%; }
.nw-ol.nw-olsum .ohero { padding: 10px 14px 9px; border-radius: 14px; }
/* The art shrinks by resizing the svg itself (not a transform, which would
   leave its layout box behind); the face and the Now tag are absolutely placed
   against that box, so they get explicit compact positions to match. */
.nw-ol.nw-olsum .serverwrap { margin-bottom: 6px; }
.nw-ol.nw-olsum .serverwrap > svg { width: 88px; height: 54px; }
.nw-ol.nw-olsum .serverwrap .moodface { width: 31px; height: 31px; }
.nw-ol.nw-olsum .serverwrap .nowtag { left: 23px; bottom: 3px; font-size: 8px; padding: 2px 4px; }
.nw-ol.nw-olsum .ocap .ores { margin-top: 0; font-size: 11.5px; padding: 4px 10px; }
/* 9px keeps "Needs attention" on one line at panel width — a wrapped band
   costs another 13px of a pane that also holds the composer. */
.nw-ol.nw-olsum .bands { gap: 4px; margin-top: 8px; }
.nw-ol.nw-olsum .band { font-size: 9px; letter-spacing: .01em; padding: 6px 2px; border-radius: 8px; }
/* One tile, full width — the page's two-column grid isn't used here. */
.nw-ol.nw-olsum .tile { margin-top: 8px; padding: 12px 14px; }
.nw-ol.nw-olsum .tile .tv { font-size: 23px; margin: 7px 0 2px; }
.nw-ol.nw-olsum .tile .tv .tsev { font-size: 12px; margin-left: 6px; }
.nw-ol.nw-olsum .tile .sevbar { height: 5px; margin: 7px 0 5px; }
.nw-ol.nw-olsum .tile .more2 { margin-top: 6px; }
/* Footer link into the full Outlook tab. */
.nw-ol.nw-olsum .olsum-open { display: block; width: 100%; margin-top: 8px; padding: 8px 12px; background: var(--card); border: 1px solid var(--line); border-radius: 11px; color: var(--accent); font-family: var(--font-body); font-size: 12.5px; font-weight: 600; text-align: center; cursor: pointer; transition: border-color .15s; }
.nw-ol.nw-olsum .olsum-open:hover { border-color: var(--line2); }

/* --- TEASER (<OutlookTeaser>) -----------------------------------------------
 * The widget's Outlook tab: the live hero and the posture bands at page size,
 * then a card that hands off to the full Outlook in the dashboard. Overrides
 * and the card only — colours, tone classes and the light flip come from the
 * blocks above, so the tab and the page cannot drift apart.
 * Art on the left, copy on the right at every width — sized so the hero, the
 * bands and the whole card fit the default 400×600 panel with no scrolling;
 * the container query only scales the card up when the panel is resized
 * wide (a host without container queries keeps the compact sizing). */
.nw-ol.nw-olteaser { width: 100%; container-type: inline-size; }
.nw-ol.nw-olteaser .olt-cta { display: flex; align-items: center; gap: 14px; margin-top: 13px; padding: 14px 16px; border-radius: 16px; background: var(--olt-panel); border: 1px solid var(--line); }
.nw-ol.nw-olteaser .olt-art { display: block; flex: 0 0 38%; width: 38%; max-width: 250px; height: auto; }
.nw-ol.nw-olteaser .olt-copy { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.nw-ol.nw-olteaser .olt-title { font-family: var(--font-display); font-size: 18px; font-weight: 700; line-height: 1.2; letter-spacing: -.01em; color: var(--ink); }
.nw-ol.nw-olteaser .olt-sub { font-size: 12.5px; line-height: 1.35; color: var(--soft); margin-top: 4px; }
/* Brand-gradient pill, the same one as the widget's "Open in app" launcher —
   `--nw-primary` / `--nw-accent` are the widget's tokens, the fallbacks are
   their values for a host that doesn't declare them. Theme-invariant. */
.nw-ol.nw-olteaser .olt-open { display: grid; grid-template-columns: 18px 1fr 16px; align-items: center; gap: 8px; width: 100%; margin-top: 12px; padding: 10px 12px; border: none; border-radius: 12px; cursor: pointer; color: #fff; background: linear-gradient(135deg, var(--nw-primary, #6366f1) 0%, var(--nw-accent, #8b5cf6) 100%); font-family: var(--font-body); font-size: 14px; font-weight: 600; line-height: 1.2; box-shadow: 0 2px 10px rgba(99, 102, 241, .32); transition: box-shadow .12s ease, filter .12s ease, transform .12s ease; }
.nw-ol.nw-olteaser .olt-open .olt-open-label { text-align: center; }
.nw-ol.nw-olteaser .olt-open svg { flex: none; }
.nw-ol.nw-olteaser .olt-open:hover { filter: brightness(1.07); box-shadow: 0 4px 14px rgba(99, 102, 241, .42); transform: translateY(-1px); }
.nw-ol.nw-olteaser .olt-open:active { filter: brightness(.97); transform: none; box-shadow: 0 1px 4px rgba(99, 102, 241, .35); }
.nw-ol.nw-olteaser .olt-open:focus-visible { outline: 2px solid var(--nw-primary, #6366f1); outline-offset: 2px; }
@container (min-width: 520px) {
  .nw-ol.nw-olteaser .olt-cta { gap: 20px; padding: 20px 26px; }
  .nw-ol.nw-olteaser .olt-art { flex-basis: 40%; width: 40%; }
  .nw-ol.nw-olteaser .olt-title { font-size: 21px; }
  .nw-ol.nw-olteaser .olt-sub { font-size: 13.5px; }
  .nw-ol.nw-olteaser .olt-open { margin-top: 16px; padding: 12px 16px; font-size: 15px; gap: 10px; }
}
@media (prefers-reduced-motion: reduce) {
  .nw-ol.nw-olteaser .olt-open { transition: none; }
  .nw-ol.nw-olteaser .olt-open:hover { transform: none; }
}

/* --- NARROW VIEWPORTS (phones) ---------------------------------------------
 * Same treatment the chat page gets at 560px: reclaim gutters, drop the
 * side-by-side grids to one column, shrink the chrome that only exists to
 * separate things there is no room to separate. Viewport-based (not container)
 * on purpose — the widget's panel is already a narrow, hand-tuned canvas on
 * desktop, and it inherits these rules exactly when the phone IS the panel. */
@media (max-width: 560px) {
  .nw-ol .ohero { padding: 20px 14px; border-radius: 14px; }
  .nw-ol .bands { gap: 4px; }
  .nw-ol .band { font-size: 10px; padding: 7px 2px; border-radius: 8px; }
  .nw-ol .envh { padding: 12px 13px 10px; }
  /* Tiles stack — two 16px-padded cards side by side leave no room for a
     30px number plus its severity word. */
  .nw-ol .tiles { grid-template-columns: 1fr; gap: 10px; }
  .nw-ol .tile { padding: 14px; }
  /* The posture row tightens up; its sentence wraps rather than shrinking. */
  .nw-ol .insight { flex-wrap: wrap; gap: 10px; padding: 12px 13px; }
  .nw-ol .insight .gl { width: 32px; height: 32px; border-radius: 9px; }

  /* The 7-day strip stays seven columns (it IS the week) — the cells shrink. */
  .nw-ol .days2 { gap: 4px; }
  .nw-ol .d2 { padding: 9px 1px; gap: 6px; border-radius: 10px; }
  .nw-ol .d2 .dw { font-size: 9.5px; letter-spacing: .02em; }
  .nw-ol .d2 .dn { font-size: 12.5px; }
  .nw-ol .d2 svg { width: 20px; height: 20px; }
  .nw-ol .d2 .d2emo { font-size: 17px; height: 20px; }
  .nw-ol .week2 .wt { flex-wrap: wrap; gap: 3px; }
  .nw-ol .wfoot { flex-wrap: wrap; gap: 7px; font-size: 12px; }
  .nw-ol .composer { padding: 6px 6px 6px 13px; }
  /* Drill-down dialogs: full-bleed card, no wasted scrim gutter. */
  .nw-ol-dim { padding: 10px; }
  .nw-ol-dialog { padding: 14px; border-radius: 14px; }
  .nw-ol-day-hero { padding: 13px; gap: 11px; }
  .nw-ol-day-stats { grid-template-columns: 1fr 1fr; gap: 8px; }
  .nw-ol-host-prob .nm { white-space: normal; }
}

/* --- LIGHT theme -----------------------------------------------------------
 * The widget sets `.nw-theme-light` on `.nw-root` (an ancestor of `.nw-ol`)
 * when the user's resolved theme is light; this flips the surface/ink tokens.
 * The SPA stamps the same class from theme/theme.ts, so this block is live on
 * BOTH hosts — it is not widget-only. Every lightness-dependent token flips
 * here, severity hues included. */
.nw-theme-light .nw-ol {
  --card: #ffffff;
  --line: rgba(17, 24, 39, 0.10);
  --line2: rgba(17, 24, 39, 0.17);
  --ink: #111827;
  --soft: #566069;
  --faint: #828b99;
  --accent: #2f6fd0; /* deeper blue for AA contrast on white */
  --chart-third: #4a3aa7; /* darker violet step for the white surface */
  /* Severity hues MUST flip too. `.nw-ol` declares its own dark --green/--amber
     /--red above, which shadows :root[data-theme='light'] for everything inside
     this subtree, so omitting them here does not "inherit the light values" —
     it pins the dark ones. These are DESIGN.md's day values, all >=5.3:1 on
     white; do not lighten them. */
  --green: #187a4e;
  --amber: #8a5a0b;
  --red: #b3372f;
  --orange: #a0521c;
  --sev4: #b3452c;
  --sev5: #a8332e;
  --track: rgba(17, 24, 39, 0.08);
  --inset: rgba(17, 24, 39, 0.025);
  --recess: rgba(17, 24, 39, 0.04);
  --chip-bg: var(--nw-chip-bg, rgba(17, 24, 39, .03));
  --code-bg: var(--nw-code-bg, rgba(17, 24, 39, .07));
  --hover: var(--nw-hover, rgba(17, 24, 39, .05));
  /* A near-black wash over a white page reads as a bug, not a scrim. */
  --scrim: rgba(17, 24, 39, .38);
  /* Teaser card on white: a lavender panel, a paler halo, grey placeholders. */
  --olt-panel: #f3f2fe;
  --olt-halo: #e8e4ff;
  --olt-ph: #ececfb;
}

/* A card whose widest element is a sentence doesn't want the 760px evidence
   width. */
.nw-ol-dialog.narrow { width: min(520px, 100%); }

/* --- keyboard, pointer and motion floor -----------------------------------
 * Added after a design critique found no authored focus style anywhere in
 * this surface and 11 of 12 controls under the 24px target minimum. */

.nw-ol .insight .ibody { display: block; width: 100%; margin: 0; padding: 0; }
.nw-ol .insight .ibody-static { cursor: default; }
.nw-ol .insight .ibody .ih { display: block; }

/* One focus ring for the whole surface, matching the SPA's own inset pattern.
   Applied to :focus-visible so pointer users never see it. */
.nw-ol-dlg-close:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--accent) 55%, transparent), 0 0 0 1px color-mix(in srgb, var(--accent) 55%, transparent);
  border-radius: 7px;
}

/* The dialog card takes programmatic focus on open; that is not a control and
   must not paint a ring. */
.nw-ol-dialog:focus,
.nw-ol-dialog:focus-visible { outline: none; }

/* The drawer and detail chrome animated on open with no reduced-motion
   fallback — the only two animations in this file that lacked one. */
@media (prefers-reduced-motion: reduce) {
  .nw-ol-dim,
.nw-ol-dialog { animation: none; }
}

